import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { POSTS, MERGED_POSTS, type Post } from "@/data/posts";
import { useReveal } from "@/hooks/use-reveal";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { ORGANIZATION_SCHEMA, absoluteImageUrl } from "@/lib/schema-org";
export const Route = createFileRoute("/actualites/$slug")({
  loader: ({ params }) => {
    const merged = MERGED_POSTS[params.slug];
    if (merged) {
      throw redirect({
        to: "/actualites/$slug",
        params: { slug: merged },
        statusCode: 301,
        throw: true,
      });
    }
    const post = POSTS.find((p) => p.slug === params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.post as Post | undefined;
    if (!p) return { meta: [{ title: "Article" }] };

    const scripts: { type: string; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: p.title,
          description: p.excerpt,
          image: p.cover,
          datePublished: p.date,
          dateModified: p.date,
          author: { "@type": "Person", name: "DJ Val", url: "https://dj-val.fr/a-propos" },
          publisher: ORGANIZATION_SCHEMA,
          mainEntityOfPage: `https://dj-val.fr/actualites/${p.slug}`,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "Actualités", item: "https://dj-val.fr/actualites" },
            { "@type": "ListItem", position: 3, name: p.title, item: `https://dj-val.fr/actualites/${p.slug}` },
          ],
        }),
      },
    ];
    if (p.faq && p.faq.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: p.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    return {
      meta: [
        { title: p.seoTitle ?? `${p.title} | DJ Val` },
        { name: "description", content: metaDesc(p.excerpt) },
        ...(p.noindex ? [{ name: "robots", content: "noindex,follow" }] : []),

        { property: "og:title", content: p.title },
        { property: "og:description", content: metaDesc(p.excerpt) },
        { property: "og:image", content: absoluteImageUrl(p.cover) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dj-val.fr/actualites/${p.slug}` },
        { property: "article:published_time", content: p.date },
        { property: "article:author", content: "DJ Val" },
        { property: "article:section", content: p.category },
      ],
      links: [{ rel: "canonical", href: `https://dj-val.fr/actualites/${p.slug}` }],
      scripts,
    };
  },
  notFoundComponent: () => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="font-serif-display text-4xl">Article introuvable</h1>
        <Link to="/actualites" className="mt-4 inline-block text-[var(--gold)] underline">Retour au blog</Link>
      </div>
    </div>
  ),
  component: PostPage,
});

function PostPage() {
  useReveal();
  const { post } = Route.useLoaderData();
  const p: Post = post;
  const related = POSTS.filter((x) => x.slug !== p.slug).slice(0, 3);

  return (
    <div className="bg-background text-muted-foreground">
      <section className="relative pt-32 pb-12 px-6 text-center">
        <div className="max-w-3xl mx-auto reveal space-y-5">
          <nav aria-label="Fil d'Ariane" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/actualites" className="hover:text-[var(--gold)]">Actualités</Link>
          </nav>
          <p className="eyebrow text-[var(--gold)]">{p.category} · {p.readTime}</p>
          <h1 className="font-serif-display text-foreground text-3xl sm:text-4xl lg:text-5xl leading-tight">{p.title}</h1>
          <span className="gold-rule mx-auto !w-12" />
          <p className="text-sm text-foreground/85">
            Par <Link to="/a-propos" className="underline decoration-[var(--gold)] underline-offset-4">DJ Val</Link>
            {" · "}
            {new Date(p.date).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </section>
      <ReviewsProof />

      <section className="px-6 max-w-5xl mx-auto">
        <div className="aspect-[16/9] overflow-hidden border border-[var(--gold-line)]">
          <img loading="lazy" decoding="async" src={p.cover} alt={p.title} className="h-full w-full object-cover" />
        </div>
      </section>

      <article className="py-16 px-6 max-w-3xl mx-auto space-y-6">
        <p className="text-lg text-foreground/90 font-light italic leading-relaxed">{p.excerpt}</p>

        {p.tldr && p.tldr.length > 0 && (
          <aside className="border-l-2 border-[var(--gold)] bg-card/60 p-6 my-8">
            <p className="eyebrow text-[var(--gold)] mb-3">En résumé</p>
            <ul className="space-y-2">
              {p.tldr.map((t) => (
                <li key={t} className="flex gap-3 text-sm text-foreground/85 leading-relaxed">
                  <span className="text-[var(--gold)] mt-0.5">→</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {p.content.map((block, i) => {
          if (block.type === "h2") return <h2 key={i} className="font-serif-display text-3xl text-foreground mt-10">{block.text}</h2>;
          if (block.type === "h3") return <h3 key={i} className="font-serif-display text-xl text-foreground mt-6">{block.text}</h3>;
          if (block.type === "quote")
            return (
              <blockquote key={i} className="border-l-2 border-[var(--gold)] pl-6 py-2 italic text-lg text-foreground/90 my-8 font-serif-display">
                {block.text}
              </blockquote>
            );
          if (block.type === "ul")
            return (
              <ul key={i} className="space-y-3">
                {block.items?.map((it) => (
                  <li key={it} className="flex gap-3 text-base text-muted-foreground font-light">
                    <span className="text-[var(--gold)]">✦</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            );
          return <p key={i} className="text-base text-muted-foreground leading-relaxed font-light">{block.text}</p>;
        })}
      </article>

      {p.faq && p.faq.length > 0 && (
        <section className="py-16 px-6 max-w-3xl mx-auto border-t border-[var(--gold-line)]">
          <div className="text-center mb-10">
            <p className="eyebrow text-[var(--gold)]">FAQ</p>
            <h2 className="font-serif-display text-3xl text-foreground mt-3">Questions fréquentes</h2>
          </div>
          <div className="space-y-3">
            {p.faq.map((f) => (
              <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-5">
                <summary className="cursor-pointer font-serif-display text-lg text-foreground flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="py-16 px-6 text-center border-t border-[var(--gold-line)]">
        <p className="text-sm text-muted-foreground mb-4 italic font-light">
          Cet article vous a éclairé ? Parlons de votre projet.
        </p>
        <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
          Demander un devis gratuit
        </Link>
      </section>

      <section className="py-20 px-6 max-w-7xl mx-auto border-t border-[var(--gold-line)]">
        <p className="eyebrow text-[var(--gold)] text-center">À lire aussi</p>
        <div className="grid gap-8 sm:grid-cols-3 mt-10">
          {related.map((r) => (
            <Link key={r.slug} to="/actualites/$slug" params={{ slug: r.slug }} className="group border border-[var(--gold-line)] bg-card overflow-hidden block">
              <div className="aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" src={r.cover} alt={r.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <h3 className="font-serif-display text-lg text-foreground group-hover:text-[var(--gold)] transition-colors leading-snug">{r.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
